import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RefreshCw, Users } from "lucide-react";
import { API } from "@/lib/api";
import TopBar from "@/components/TopBar";

export default function JoinSmallTable() {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualKey, setManualKey] = useState("");

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/tavern-rooms`);
      setTables(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to fetch tables.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    const id = setInterval(fetchList, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = (key) => {
    if (!key) return;
    navigate(`/tavern/room/${key.toUpperCase()}`);
  };

  const submitManual = (e) => {
    e.preventDefault();
    const k = manualKey.trim().toUpperCase();
    if (!k) return;
    join(k);
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex flex-col" data-testid="join-small-table-page">
      <div className="absolute inset-0 tapestry-grid pointer-events-none opacity-50" />
      <TopBar
        theme="tavern"
        onBack={() => navigate("/tavern")}
        backLabel="Tavern"
        testId="join-small-back-button"
        right={
          <button
            onClick={fetchList}
            className="arcade-btn arcade-btn-ghost !px-3 !py-2 text-xs"
            data-testid="join-small-refresh-button"
            aria-label="Refresh tables"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        }
      />
      <div className="relative z-10 max-w-2xl mx-auto px-6 pt-24 pb-12 w-full">
        <div className="text-center mb-6">
          <h1
            className="font-display text-3xl md:text-4xl font-bold tracking-tight"
            style={{ color: "#E5C870", textShadow: "0 1px 0 rgba(0,0,0,0.7), 0 0 14px rgba(201,162,74,0.5)" }}
          >
            Pull Up a Stool
          </h1>
          <p className="mt-3 italic font-body text-base" style={{ color: "#E5C870" }}>
            Pick from the tables in session — or whisper a chamber-key.
          </p>
        </div>

        <div className="arcade-panel p-4 mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-red-800 mb-3 font-display">
            Tables in Session
          </div>
          {loading && tables.length === 0 ? (
            <div className="text-stone-700 italic text-sm py-4 text-center" data-testid="join-small-loading">
              Peeking through the keyholes…
            </div>
          ) : error ? (
            <div className="text-red-700 italic text-sm py-4 text-center" data-testid="join-small-error">
              {error}
            </div>
          ) : tables.length === 0 ? (
            <div className="text-stone-700 italic text-sm py-4 text-center" data-testid="join-small-empty">
              No small tables are in session. Why not host one yourself?
            </div>
          ) : (
            <ul className="divide-y divide-amber-700/30" data-testid="join-small-list">
              {tables.map((t) => (
                <li key={t.key}>
                  <button
                    onClick={() => join(t.key)}
                    className="w-full flex items-center gap-3 px-2 py-3 hover:bg-amber-100/40 transition-colors text-left"
                    data-testid={`join-small-row-${t.key}`}
                  >
                    <div className="flex-1">
                      <div className="font-display text-base text-stone-900 truncate">
                        {t.name}
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-stone-700 text-sm font-mono shrink-0">
                      <Users size={14} /> {t.members}
                    </span>
                    <ArrowRight size={16} className="text-amber-700 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {tables.length > 0 && (
          <form onSubmit={submitManual} className="arcade-panel p-4 flex items-center gap-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-800 font-display shrink-0">
              Chamber-Key
            </div>
            <input
              value={manualKey}
              onChange={(e) => setManualKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
              placeholder="XXXX"
              className="arcade-input flex-1 text-center tracking-[0.4em] font-playfair text-lg"
              data-testid="join-small-manual-key-input"
            />
            <button
              type="submit"
              disabled={!manualKey.trim()}
              className="arcade-btn arcade-btn-magenta !px-4 !py-2 text-xs disabled:opacity-50"
              data-testid="join-small-manual-submit"
            >
              Enter
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
